import { Fragment, forwardRef } from 'react';
import { Outlet } from 'react-router-dom';

import pick from 'lodash/pick';

import { HeaderTabs } from '@backstage/core-components';
import { attachComponentData } from '@backstage/core-plugin-api';

import {
  PageLayout,
  PropsBridgeProvider,
  usePropsBridge,
} from '@spotify/confidence-core-react';

import { PageRootBridgeProps } from '../types';
import { PageRouteRef } from '../utils';
import { PAGE_CONTENT_KEY, usePageContentChildren } from './composition';
import { resolvePageHeaderLayoutProps } from './resolvePageHeaderLayoutProps';
import type {
  PageContentBridgeProps,
  PageContentLayoutProps,
  PageContentProps,
} from './types';
import { useActionBar } from './useActionBar';
import { useContentOffsets } from './useContentOffsets';
import { useTabs } from './useTabs';

export const getPageContentLayoutProps = (
  props: PageContentProps,
): PageContentLayoutProps =>
  pick(props, [
    'contentOverflow',
    'floatingHeader',
    'fullViewport',
    'narrow',
    'noPadding',
    'scrollButton',
    'stickyHeaderTabs',
  ]);

export const ContentTabsHeader = ({
  routeRefs,
}: {
  routeRefs: PageRouteRef[];
}) => {
  const { tabs, selectedIndex } = useTabs(routeRefs);
  return <HeaderTabs selectedIndex={selectedIndex} tabs={tabs} />;
};

export const Content = forwardRef<HTMLDivElement, PageContentProps>(
  (props, ref) => {
    const {
      children,
      headerTabs: headerTabsProp,
      headerTabsRef: headerTabsRefProp,
    } = props;
    const layoutProps = getPageContentLayoutProps(props);
    const bridgeProps = usePropsBridge<
      PageRootBridgeProps & { contentTopOffset?: number }
    >();

    const { wrapperElements } = bridgeProps;

    const {
      sideBarLeft,
      sideBarRight,
      actionBar,
      tabsRouteRefs,
      mainChildren,
    } = usePageContentChildren(children);

    const {
      actionBarRef,
      tabsRef,
      isNested,
      actionBarTopOffset,
      contentTopOffset,
    } = useContentOffsets(bridgeProps.contentTopOffset);

    const renderedActionBar = useActionBar({
      wrapperElements,
      actionBar,
      actionBarRef,
      actionBarTopOffset,
    });

    const body = (
      <PropsBridgeProvider<PageContentBridgeProps>
        {...bridgeProps}
        contentTopOffset={contentTopOffset}
      >
        {mainChildren}
        {tabsRouteRefs && <Outlet />}
      </PropsBridgeProvider>
    );

    if (isNested) {
      return (
        <Fragment>
          {renderedActionBar}
          {body}
        </Fragment>
      );
    }

    const headerTabs = tabsRouteRefs ? (
      <ContentTabsHeader routeRefs={tabsRouteRefs} />
    ) : (
      headerTabsProp
    );
    const headerTabsRef = tabsRouteRefs ? tabsRef : headerTabsRefProp;
    const headerLayoutProps = resolvePageHeaderLayoutProps({
      page: bridgeProps,
      content: props,
    });

    return (
      <PageLayout
        ref={ref}
        {...layoutProps}
        {...headerLayoutProps}
        sideBarLeft={sideBarLeft}
        sideBarRight={sideBarRight}
        headerTabs={headerTabs}
        headerTabsRef={headerTabsRef}
      >
        {renderedActionBar}
        {body}
      </PageLayout>
    );
  },
);
Content.displayName = 'Content';

attachComponentData(Content, PAGE_CONTENT_KEY, true);

export type { PageContentProps } from './types';
